// React Query hooks for the Interviewer Prep surface. The guide and any in-flight message poll at
// 1.5s until they reach a terminal status; everything else is a one-shot fetch/mutation.

import { useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  applyPrepEdit,
  getPrepGuide,
  getPrepMessage,
  isTerminal,
  listPrepMessages,
  PrepGuide,
  PrepMessage,
  retryPrepMessage,
  sendPrepMessage,
} from './api';

const POLL_MS = 1500;
// The apply edit is an LLM call that can sit behind a Sidekiq queue, so give it a generous window
// (~90s) — the poll stops the instant the edit lands, this is only the worst-case ceiling.
const APPLY_POLL_TRIES = 60;

type InterviewId = number | string | undefined;

export function usePrepGuide(interviewId: InterviewId, enabled = true) {
  return useQuery<PrepGuide>({
    queryKey: ['prep-guide', interviewId],
    queryFn: () => getPrepGuide(interviewId!),
    enabled: enabled && Boolean(interviewId),
    retry: 1,
    // Poll while generating; stop once terminal OR the request errors (don't hammer a failing endpoint).
    refetchInterval: (query) => {
      if (query.state.status === 'error') return false;
      return isTerminal(query.state.data?.status) ? false : POLL_MS;
    },
    // Once completed the poll stops, so the cached brief can drift if the guide changes out-of-band
    // (an apply from another tab, a regeneration). Refetch when the tab/window regains focus so the
    // brief re-syncs with the DB instead of silently showing a stale plan.
    refetchOnWindowFocus: true,
  });
}

export function usePrepMessages(interviewId: InterviewId, enabled = true) {
  return useQuery<PrepMessage[]>({
    queryKey: ['prep-messages', interviewId],
    queryFn: () => listPrepMessages(interviewId!),
    enabled: enabled && Boolean(interviewId),
  });
}

// Polls one message until it reaches a terminal status, surfacing its live progress label.
export function usePrepMessagePoll(
  interviewId: InterviewId,
  messageId?: number | null
) {
  return useQuery<PrepMessage>({
    queryKey: ['prep-message', interviewId, messageId],
    queryFn: () => getPrepMessage(interviewId!, messageId!),
    enabled: Boolean(interviewId) && Boolean(messageId),
    retry: 1,
    refetchInterval: (query) => {
      if (query.state.status === 'error') return false;
      return isTerminal(query.state.data?.status) ? false : POLL_MS;
    },
  });
}

export function useSendPrepMessage(interviewId: InterviewId) {
  const qc = useQueryClient();
  return useMutation<PrepMessage, unknown, string>({
    mutationFn: (query: string) => sendPrepMessage(interviewId!, query),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['prep-messages', interviewId] });
    },
  });
}

export function useRetryPrepMessage(interviewId: InterviewId) {
  return useMutation<PrepMessage, unknown, number>({
    mutationFn: (id: number) => retryPrepMessage(interviewId!, id),
  });
}

// Apply an agreed change, then poll the guide until it lands (last_edited_at moves),
// pushing each fresh guide into the shared cache so the left brief reflows in place. The guide stays
// `completed` throughout, so the brief never blanks. Returns whether the edit actually persisted, so
// the caller can surface a failure instead of falsely showing "Applied" when the worker never wrote.
export function useApplyPrepEdit(interviewId: InterviewId) {
  const qc = useQueryClient();
  const [isApplying, setIsApplying] = useState(false);

  const apply = useCallback(
    async (messageId: number): Promise<boolean> => {
      if (!interviewId) return false;
      setIsApplying(true);
      try {
        // Baseline from a real fetch so we wait for the worker's actual edit. The endpoint is fresh
        // (verified — no replica lag), so once the edit lands the next poll sees the new timestamp.
        const before = (await getPrepGuide(interviewId)).last_edited_at;
        await applyPrepEdit(interviewId, messageId);
        for (let i = 0; i < APPLY_POLL_TRIES; i++) {
          await new Promise((resolve) => setTimeout(resolve, POLL_MS));
          const fresh = await getPrepGuide(interviewId);
          if (fresh.last_edited_at && fresh.last_edited_at !== before) {
            qc.setQueryData(['prep-guide', interviewId], fresh);
            await qc.invalidateQueries({ queryKey: ['prep-guide'] });
            return true;
          }
        }
        // The worker never moved the timestamp within the window — it failed or never ran. Report it
        // so the UI can offer a retry rather than silently claiming success.
        return false;
      } finally {
        setIsApplying(false);
      }
    },
    [interviewId, qc]
  );

  return { apply, isApplying };
}
